import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '@/components/layout/AppLayout';
import { Footer } from '@/components/layout/Footer';

const Terms: React.FC = () => {
  const { lang } = useParams<{ lang?: string }>();
  const { t } = useTranslation('common');
  const code = (lang || 'en').split('-')[0];

  return (
    <AppLayout>
      <Helmet>
        <title>Terms of Service | JiGSO EX Hub</title>
        <meta name="description" content="Terms of Service for JiGSO EX Hub - Read our terms and conditions for using our employee experience tools and services." />
        <link rel="canonical" href={`${window.location.origin}/${code}/terms`} />
      </Helmet>
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-background via-secondary/20 to-accent/20 pt-16 pb-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                Terms of Service — JiGSO EX Hub
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Please read these terms carefully before using our services.
              </p>
              <div className="mt-6 text-sm text-muted-foreground">
                <strong>Effective date:</strong> January 2025
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-lg max-w-none">
                <div className="space-y-8">
                  {/* Acceptance of Terms */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      By using JiGSO EX Hub, you agree to these Terms of Service and our Privacy Policy.
                    </p>
                  </div>

                  {/* Service Description */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">2. Service Description</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      JiGSO EX Hub is a platform operated by JiGSO BVBA to provide HR tools, templates, and resources for employee experience improvement.
                    </p>
                    <p className="text-muted-foreground leading-relaxed mt-3">
                      Features include survey creation, template libraries, and analytics.
                    </p>
                  </div>

                  {/* User Accounts */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">3. User Accounts</h2>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>You may create a free account to save templates, access history, and personalize results.</li>
                      <li>You are responsible for safeguarding your account credentials.</li>
                      <li>We may suspend or terminate accounts that violate these terms.</li>
                    </ul>
                  </div>

                  {/* Acceptable Use */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">4. Acceptable Use</h2>
                    <p className="text-muted-foreground leading-relaxed mb-3">
                      You agree not to:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>Use the platform for unlawful purposes</li>
                      <li>Upload or share offensive, discriminatory, or rights-violating content</li>
                      <li>Attempt to disrupt, reverse-engineer, or exploit our systems</li>
                    </ul>
                  </div>

                  {/* Intellectual Property */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">5. Intellectual Property</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      All platform content, branding, and features are owned by JiGSO unless otherwise stated. You may not copy, modify, or distribute them without written permission.
                    </p>
                  </div>

                  {/* Disclaimer of Warranties */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">6. Disclaimer of Warranties</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      The service is provided "as is" without warranties of any kind. We do not guarantee uninterrupted or error-free operation.
                    </p>
                  </div>

                  {/* Limitation of Liability */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">7. Limitation of Liability</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      To the extent permitted by law, JiGSO BV is not liable for indirect, incidental, or consequential damages arising from your use of the EX Hub.
                    </p>
                  </div>

                  {/* Changes to Terms */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">8. Changes to Terms</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      We may update these terms at any time. Continued use of the platform after updates means you accept the new terms.
                    </p>
                  </div>

                  {/* Governing Law */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">9. Governing Law</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      These terms are governed by Belgian law. Any disputes will be handled in the courts of Brussels, Belgium.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      
      <Footer />
    </AppLayout>
  );
};

export default Terms; 